/**
 * Loads service available slots for a visible month and exposes picker constraints.
 */

import { useCallback, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useFindServiceAvailableSlotsQuery } from '@/api/mcp-appointments';
import { buildServiceMonthRange, mapServiceSlotsToAvailableSlots } from '@/utils/serviceBooking';
import { useSlotScheduleCore } from './useSlotScheduleCore';

interface UseServiceAvailableSlotsScheduleProps {
  serviceId: string | null | undefined;
  serviceTimezone: string;
  primaryResourceId: string | null | undefined;
  needsPrimaryResource: boolean;
  startTime: Dayjs | null | undefined;
  endTime: Dayjs | null | undefined;
  visibleMonth: Dayjs;
  hasCommittedStartTime: boolean;
  treatAsValidSlot?: { start: Dayjs; end: Dayjs } | null;
  enabled?: boolean;
  slotAlignmentMinutes?: number;
  excludeAppointmentId?: string;
}

const MAX_SLOTS_REQUEST = 50000;
const DEFAULT_SLOT_ALIGNMENT = 15;

/**
 * Fetches service slots for the visible month and derives picker disable callbacks and selection state.
 * @returns Same shape as `useAvailableSlotsSchedule`, plus `slotsQueryReady`.
 */
export const useServiceAvailableSlotsSchedule = ({
  serviceId,
  serviceTimezone,
  primaryResourceId,
  needsPrimaryResource,
  startTime,
  endTime,
  visibleMonth,
  hasCommittedStartTime,
  treatAsValidSlot = null,
  enabled = true,
  slotAlignmentMinutes: slotAlignmentMinutesProp,
  excludeAppointmentId
}: UseServiceAvailableSlotsScheduleProps) => {
  const slotAlignmentMinutes = slotAlignmentMinutesProp ?? DEFAULT_SLOT_ALIGNMENT;

  const slotsQueryReady = useMemo(() => {
    if (!serviceId) {
      return false;
    }
    if (needsPrimaryResource && !primaryResourceId) {
      return false;
    }
    return true;
  }, [serviceId, needsPrimaryResource, primaryResourceId]);

  const queryParams = useMemo(() => {
    if (!slotsQueryReady) {
      return null;
    }
    const range = buildServiceMonthRange(visibleMonth);
    if (!range) {
      return null;
    }
    return {
      startTime: range.startTime,
      endTime: range.endTime,
      maxSlots: MAX_SLOTS_REQUEST,
      slotAlignmentMinutes,
      ...(primaryResourceId ? { primaryResourceId } : {}),
      ...(excludeAppointmentId ? { excludeAppointmentId } : {})
    };
  }, [slotsQueryReady, visibleMonth, slotAlignmentMinutes, primaryResourceId, excludeAppointmentId]);

  const { data: rawSlots, isLoading, isFetching } = useFindServiceAvailableSlotsQuery(
    serviceId || '',
    queryParams,
    enabled && slotsQueryReady && !!queryParams
  );

  const slots = useMemo(() => mapServiceSlotsToAvailableSlots(rawSlots), [rawSlots]);

  const timezone = serviceTimezone || 'UTC';
  const truncated = (rawSlots?.length ?? 0) >= MAX_SLOTS_REQUEST;

  const disabledDate = useCallback((current: Dayjs | null) => {
    if (!current) {
      return false;
    }
    return current.startOf('day').isBefore(dayjs(), 'day');
  }, []);

  const schedule = useSlotScheduleCore({
    slots,
    isLoading,
    isFetching,
    hasSlotsResult: slotsQueryReady,
    timezone,
    slotAlignmentMinutes,
    visibleMonth,
    startTime,
    endTime,
    hasCommittedStartTime,
    treatAsValidSlot,
    disabledDate
  });

  // `slots` is re-exposed from the mapped array so its type stays non-optional for service callers.
  return { ...schedule, truncated, slots, slotsQueryReady };
};
